import { App, Form, Select } from "antd"
import type { FormInstance } from "antd"
import React, { type Ref } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"
import {
  type RichTextEditorHandle,
  RichTextEditor
} from "../../../../components/RichTextEditor/RichTextEditor.tsx"
import { isRichTextEmpty } from "../../../../components/RichTextEditor/rich-text-html.ts"
import { unwrapEnvelope } from "../../../../utils/api/unwrap-envelope.ts"
import { uploadEmailTemplateImage } from "../../../EmailTemplates/api/services.ts"
import type { AutomationFormValues } from "../../helpers/form-values"
import type { AutomationOptions } from "../../types/automation.types"
import { TokenChips } from "../TokenChips"
import { BuilderSection } from "./BuilderSection"

const SUBJECT_MAX = 255

interface TemplateSelectProps {
  value?: string | null
  onChange?: (value: string | null) => void
  onPick: (templateId: string | null) => void
  templates: AutomationOptions["templates"]
  id?: string
  "aria-describedby"?: string
}

// Form.Item hands this its value/onChange (and the id and description that tie
// it to its label and helper); picking goes through `onPick` so a confirm can
// veto the change before the field (and the copy) is written.
function TemplateSelect({
  value,
  onPick,
  templates,
  id,
  "aria-describedby": describedBy
}: TemplateSelectProps) {
  const { t } = useTranslation()
  return (
    <Select
      id={id}
      aria-describedby={describedBy}
      value={value ?? undefined}
      allowClear
      showSearch
      optionFilterProp={"label"}
      placeholder={t("settingsAutomation.builder.message.templatePlaceholder")}
      options={templates.map((template) => ({ value: template.id, label: template.name }))}
      onChange={(next?: string) => onPick(next ?? null)}
    />
  )
}

interface MessageSectionProps {
  options: AutomationOptions
  form: FormInstance<AutomationFormValues>
  complete: boolean
  editorRef: Ref<RichTextEditorHandle>
  /** setFieldsValue is not reported through onValuesChange. */
  onProgrammaticChange: () => void
}

export function MessageSection({
  options,
  form,
  complete,
  editorRef,
  onProgrammaticChange
}: MessageSectionProps) {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()

  const applyTemplate = (templateId: string | null) => {
    const template = options.templates.find((candidate) => candidate.id === templateId)
    form.setFieldsValue(
      template
        ? { source_template: template.id, subject: template.subject, body: template.body }
        : { source_template: null }
    )
    onProgrammaticChange()
  }

  const handleTemplatePick = (templateId: string | null) => {
    const next = options.templates.find((candidate) => candidate.id === templateId)
    if (!next) {
      applyTemplate(null)
      return
    }
    const subject: string = form.getFieldValue("subject") ?? ""
    const body: string = form.getFieldValue("body") ?? ""
    const current = options.templates.find(
      (candidate) => candidate.id === form.getFieldValue("source_template")
    )
    const hasText = subject.trim() !== "" || !isRichTextEmpty(body)
    const untouchedCopy = !!current && current.subject === subject && current.body === body
    if (!hasText || untouchedCopy) {
      applyTemplate(next.id)
      return
    }
    modal.confirm({
      centered: true,
      title: t("settingsAutomation.builder.message.replaceTitle"),
      okText: t("settingsAutomation.builder.message.replace"),
      cancelText: t("settingsAutomation.builder.message.cancel"),
      onOk: () => applyTemplate(next.id)
    })
  }

  const handleImageUpload = async (file: File): Promise<{ url: string }> => {
    const response = await uploadEmailTemplateImage(file)
    const data = response.ok ? unwrapEnvelope<{ url: string }>(response.data) : undefined
    if (!data?.url) {
      void message.error(t("settingsAutomation.builder.message.uploadFailed"))
      throw new Error("Image upload failed")
    }
    return { url: data.url }
  }

  const insertToken = (token: string) => {
    if (editorRef && typeof editorRef === "object") editorRef.current?.insertText(token)
  }

  return (
    <BuilderSection sectionKey={"message"} complete={complete}>
      <Form.Item<AutomationFormValues>
        label={t("settingsAutomation.builder.message.templateLabel")}
        name={"source_template"}
        extra={t("settingsAutomation.builder.message.templateHelp")}
      >
        <TemplateSelect templates={options.templates} onPick={handleTemplatePick} />
      </Form.Item>

      <Form.Item<AutomationFormValues>
        label={t("settingsAutomation.builder.message.subjectLabel")}
        name={"subject"}
        rules={[
          {
            required: true,
            whitespace: true,
            message: t("settingsAutomation.builder.message.subjectRequired")
          },
          {
            max: SUBJECT_MAX,
            message: t("settingsAutomation.builder.message.subjectMax", { max: SUBJECT_MAX })
          }
        ]}
      >
        <TextInput
          placeholder={t("settingsAutomation.builder.message.subjectPlaceholder")}
          maxLength={SUBJECT_MAX}
        />
      </Form.Item>

      <Form.Item
        label={t("settingsAutomation.builder.message.bodyLabel")}
        required
        className={"mb-0"}
      >
        <div className={"mb-2"}>
          <TokenChips tokens={options.tokens} onInsert={insertToken} />
        </div>
        {/* Enter inside the body is a new line, never a submit. */}
        <div
          onKeyDown={(event) => {
            if (event.key === "Enter") event.stopPropagation()
          }}
        >
          <Form.Item<AutomationFormValues>
            name={"body"}
            noStyle
            rules={[
              {
                validator: (_, value?: string) =>
                  isRichTextEmpty(value)
                    ? Promise.reject(new Error(t("settingsAutomation.builder.message.bodyRequired")))
                    : Promise.resolve()
              }
            ]}
          >
            <RichTextEditor
              ref={editorRef}
              highlightTokens={options.tokens.map((t) => t.token)}
              value={""}
              onChange={() => undefined}
              variant={"full"}
              minHeight={200}
              ariaLabel={t("settingsAutomation.builder.message.bodyLabel")}
              onImageUpload={handleImageUpload}
            />
          </Form.Item>
        </div>
      </Form.Item>
    </BuilderSection>
  )
}
